import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2 } from 'lucide-react';

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await authAPI.login(email, password, rememberMe);
      onLogin(response.user);
      if (response.user?.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/patients');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login gagal. Silakan periksa kembali email dan kata sandi Anda.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: '👤', text: 'Penilaian nutrisi pasien individual' },
    { icon: '🍽️', text: 'Database makanan & parenteral (dapat disesuaikan)' },
    { icon: '📊', text: 'Data lengkap makro & mikronutrien' },
    { icon: '🎙️', text: 'Input recall cepat via perintah suara' },
    { icon: '✏️', text: 'Pilihan entri data recall manual' },
    { icon: '📈', text: 'Grafik recall harian dan tren 7 hari' },
    { icon: '📄', text: 'Unduh laporan format PDF dan Excel' },
    { icon: '📱', text: 'Kompatibel smartphone & tablet (PWA)' },
  ];

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(145deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
      padding: '16px 8px',
      boxSizing: 'border-box',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Soft Ambient Background Glow */}
      <div style={{
        position: 'absolute',
        top: '20%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '480px',
        height: '480px',
        background: 'radial-gradient(circle, rgba(5, 150, 105, 0.08) 0%, rgba(248, 250, 252, 0) 70%)',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      <Card style={{
        width: '100%',
        maxWidth: '420px',
        backgroundColor: '#ffffff',
        boxShadow: '0 20px 40px -15px rgba(15, 23, 42, 0.10), 0 0 1px 1px rgba(15, 23, 42, 0.04)',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Header: Logo */}
        <CardHeader style={{ padding: '16px 22px 6px', textAlign: 'center', space: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', marginBottom: '12px' }}>
            <img
              src="/assets/nutrimus-logo.png"
              alt="NUTRIMUS"
              style={{ height: '70px', width: 'auto', objectFit: 'contain', marginBottom: '10px' }}
            />
            <div style={{
              background: '#ecfdf5',
              border: '1px solid #a7f3d0',
              color: '#065f46',
              padding: '4px 14px',
              borderRadius: '20px',
              fontSize: '10.5px',
              fontWeight: 700,
              boxShadow: '0 1px 3px rgba(5, 150, 105, 0.08)',
              display: 'inline-block',
              whiteSpace: 'nowrap',
              letterSpacing: '0.4px',
              textTransform: 'uppercase'
            }}>
              Khusus Untuk Dokter Spesialis Gizi Klinik
            </div>
          </div>

          {/* Feature Box */}
          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '10px 14px',
            textAlign: 'left',
            boxShadow: 'inset 0 1px 2px rgba(15, 23, 42, 0.02)',
            marginBottom: '8px',
          }}>
            {/* Badge header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <span style={{
                background: '#0f172a',
                borderRadius: '5px',
                padding: '2.5px 8px',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: '9.5px',
                letterSpacing: '0.4px',
                textTransform: 'uppercase',
              }}>Nutrimus Recall</span>
              <span style={{ color: '#059669', fontWeight: 600, fontSize: '10px' }}>Fitur Utama</span>
            </div>

            {/* Feature list — 2 columns on wide, 1 on narrow */}
            <ul style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: '3px 8px',
            }}>
              {features.map((item, idx) => (
                <li key={idx} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '6px',
                  padding: '2px 0',
                }}>
                  <span style={{ fontSize: '11px', minWidth: '16px', marginTop: '1px' }}>{item.icon}</span>
                  <span style={{
                    fontSize: '10.5px',
                    color: '#334155',
                    fontWeight: 500,
                    lineHeight: '1.4',
                  }}>{item.text}</span>
                </li>
              ))}
            </ul>
          </div>

          <CardDescription style={{ fontSize: '12.5px', color: '#64748b', marginTop: '4px' }}>
            Masuk ke akun NUTRIMUS Anda
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent style={{ padding: '8px 22px 8px' }}>
            {error && (
              <Alert variant="destructive" style={{ marginBottom: '10px', padding: '8px 12px', borderRadius: '10px' }}>
                <AlertDescription style={{ fontSize: '12px' }}>{error}</AlertDescription>
              </Alert>
            )}

            {/* Email */}
            <div style={{ marginBottom: '10px' }}>
              <Label htmlFor="email" style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b' }}>Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="nama@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="focus-visible:ring-emerald-500 focus-visible:border-emerald-500"
                style={{ height: '38px', fontSize: '13px', marginTop: '4px', backgroundColor: '#f8fafc', borderColor: '#cbd5e1' }}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: '10px' }}>
              <Label htmlFor="password" style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b' }}>Kata Sandi</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="focus-visible:ring-emerald-500 focus-visible:border-emerald-500"
                style={{ height: '38px', fontSize: '13px', marginTop: '4px', backgroundColor: '#f8fafc', borderColor: '#cbd5e1' }}
              />
            </div>

            {/* Remember me + Forgot */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{ height: '14px', width: '14px', accentColor: '#059669' }}
                />
                <Label htmlFor="rememberMe" style={{ fontSize: '12px', fontWeight: 400, color: '#475569', cursor: 'pointer', margin: 0 }}>
                  Ingat Saya
                </Label>
              </div>
              <Link to="/forgot-password" style={{ fontSize: '12px', color: '#059669', fontWeight: 500, textDecoration: 'none' }}>
                Lupa kata sandi?
              </Link>
            </div>
          </CardContent>

          <CardFooter style={{ padding: '10px 22px 18px', flexDirection: 'column', gap: '10px' }}>
            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-all duration-150"
              disabled={loading}
              style={{
                height: '40px',
                fontSize: '13.5px',
                borderRadius: '10px',
                boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)'
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Memproses...
                </>
              ) : 'Masuk'}
            </Button>
            <p style={{ fontSize: '12px', textAlign: 'center', color: '#64748b', margin: 0 }}>
              Belum memiliki akun? {' '}
              <Link to="/register" style={{ color: '#059669', fontWeight: 600, textDecoration: 'none' }}>
                Daftar sekarang
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
